import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, Briefcase, Brain, Sparkles, Mic, Layers, IndianRupee, Clock, HelpCircle, BarChart3, ArrowRight } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../store/authStore';
import { BRAND, COURSES, ROUNDS } from '../lib/constants';

const ICONS = { TrendingUp, Briefcase, Brain };

export default function Home() {
    const { user } = useAuth();
    const [selected, setSelected] = useState(null);
    const [starting, setStarting] = useState(false);
    const [err, setErr] = useState('');
    const navigate = useNavigate();

    const startInterview = async () => {
        if (!selected) return;
        setStarting(true);
        setErr('');
        try {
            const res = await api.post('/interviews/start', { course: selected });
            navigate(`/interview/${res.data.session_id}`);
        } catch (e) {
            setErr(e.response?.data?.detail || 'Failed to start interview');
        } finally {
            setStarting(false);
        }
    };

    return (
        <div className="space-y-10" data-testid="home-page">
            {/* Hero card */}
            <motion.section
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="brand-hero-gradient overflow-hidden rounded-2xl p-8 md:p-12 text-white relative"
            >
                <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold">
                            <Sparkles className="h-3.5 w-3.5" /> Welcome back, {user?.full_name || user?.username}
                        </div>
                        <h1 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
                            {BRAND.heroTagline}
                        </h1>
                        <p className="mt-3 text-white/90 max-w-2xl text-sm md:text-base leading-relaxed">
                            {BRAND.heroSub}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {[
                                { icon: Sparkles, label: 'AI-Powered' },
                                { icon: Layers, label: '5 Rounds' },
                                { icon: Mic, label: 'Voice-Enabled' },
                                { icon: IndianRupee, label: 'Salary Practice' },
                            ].map((b) => (
                                <div
                                    key={b.label}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold"
                                >
                                    <b.icon className="h-3.5 w-3.5" /> {b.label}
                                </div>
                            ))}
                        </div>
                    </div>
                    <img
                        src={BRAND.logoUrl}
                        alt="ExcellenC"
                        className="hidden md:block h-24 w-auto bg-white/95 rounded-xl p-4 shadow-lg"
                    />
                </div>
            </motion.section>

            {/* Course selection */}
            <section>
                <div className="mb-4 flex items-end justify-between">
                    <div>
                        <h2 className="text-lg md:text-xl font-bold text-[#111827]">
                            Choose your career track
                        </h2>
                        <p className="text-sm text-[#6B7280]">
                            The interview questions will adapt to the course you pick.
                        </p>
                    </div>
                </div>

                <div className="grid gap-5 md:grid-cols-3">
                    {COURSES.map((c, idx) => {
                        const Icon = ICONS[c.icon] || TrendingUp;
                        const active = selected === c.key;
                        return (
                            <motion.button
                                type="button"
                                key={c.key}
                                data-testid={`course-card-${c.key.toLowerCase().replace(/\s+/g, '-')}`}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: idx * 0.08 }}
                                onClick={() => setSelected(c.key)}
                                className={`text-left rounded-2xl border-2 bg-white p-6 transition ${
                                    active
                                        ? 'border-[#1C3A94] shadow-lg -translate-y-1'
                                        : 'border-[#E5E7EB] hover:border-[#1C3A94]/40 hover:shadow-md'
                                }`}
                            >
                                <div
                                    className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl"
                                    style={{ background: `${c.color}18`, color: c.color }}
                                >
                                    <Icon className="h-6 w-6" />
                                </div>
                                <div className="text-lg font-bold text-[#111827]">{c.key}</div>
                                <p className="mt-1 text-sm text-[#6B7280]">{c.description}</p>
                                <div className="mt-4 flex flex-wrap gap-1.5">
                                    {c.modules.map((m) => (
                                        <span
                                            key={m}
                                            className="rounded-full border border-[#E5E7EB] bg-[#F9FAFB] px-2 py-0.5 text-[11px] font-medium text-[#6B7280]"
                                        >
                                            {m}
                                        </span>
                                    ))}
                                </div>
                                {active && (
                                    <div className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-[#1C3A94]">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#1C3A94]" />
                                        Selected
                                    </div>
                                )}
                            </motion.button>
                        );
                    })}
                </div>

                {err && (
                    <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {err}
                    </div>
                )}

                <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="text-sm text-[#6B7280]">
                        {selected ? (
                            <>Ready to start? Your interview covers 5 rounds with real-time AI evaluation.</>
                        ) : (
                            <>Select a course above to enable the “Start Interview” button.</>
                        )}
                    </div>
                    <button
                        data-testid="start-interview-btn"
                        onClick={startInterview}
                        disabled={!selected || starting}
                        className="brand-btn-primary gap-2"
                    >
                        {starting ? 'Starting…' : 'Start Interview'}
                        <ArrowRight className="h-4 w-4" />
                    </button>
                </div>
            </section>

            {/* Rounds timeline */}
            <section>
                <h2 className="text-lg md:text-xl font-bold text-[#111827]">The 5-round journey</h2>
                <p className="text-sm text-[#6B7280]">
                    Each round tests a different skill — from technical knowledge to negotiation.
                </p>
                <div className="mt-5 grid gap-4 md:grid-cols-5">
                    {ROUNDS.map((r, i) => (
                        <div
                            key={r.num}
                            className="brand-card p-4"
                        >
                            <div className="flex items-center justify-between">
                                <div className="text-xs font-bold text-[#1C3A94]">ROUND {r.num}</div>
                                {i < ROUNDS.length - 1 && (
                                    <ArrowRight className="h-4 w-4 text-[#9CA3AF] hidden md:block" />
                                )}
                            </div>
                            <div className="mt-2 text-sm font-bold text-[#111827]">{r.name}</div>
                            <div className="mt-1 text-xs text-[#6B7280] leading-relaxed">{r.desc}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Quick stats */}
            <section className="grid gap-4 md:grid-cols-3">
                {[
                    { icon: Clock, k: '~45 min', v: 'Full session' },
                    { icon: HelpCircle, k: '15', v: 'AI-generated questions' },
                    { icon: BarChart3, k: '1-10', v: 'AI scoring per answer' },
                ].map((s) => (
                    <div key={s.v} className="brand-card p-5 flex items-center gap-4">
                        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#F1F5FD] text-[#1C3A94]">
                            <s.icon className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="text-base font-bold text-[#111827]">{s.k}</div>
                            <div className="text-xs text-[#6B7280]">{s.v}</div>
                        </div>
                    </div>
                ))}
            </section>
        </div>
    );
}
